import { trackFeatureUsage } from "../utils/analytics";
import { capturePostHogEvent } from "../posthog";

/**
 * CRM: avance del lead por el embudo comercial.
 *
 * Solo se instrumentan las secciones `*Live` (las que hablan con el backend
 * real). Las secciones de maqueta (`negocios`, `contactos`, `leads`,
 * `tareas`, `marketing`) montan arreglos en memoria: medir "mover una etapa"
 * ahi seria medir datos inventados.
 *
 * Privacidad: el lead ES un dato personal (nombre, correo, WhatsApp, empresa,
 * cargo, el problema que escribio). No sale nada de eso, ni el motivo de
 * perdida que el vendedor teclea en el dialogo. Viajan estado, origen,
 * producto, calificacion y conteos.
 */

export type LeadMoveMethod = "drag" | "select";

const FEATURE = "crm_leads";

/**
 * Cambio de estado del lead desde el listado.
 *
 * `source` (landing, meta_ads, import) es la propiedad que cierra el circulo
 * con marketing: que canal trae leads que de verdad avanzan.
 */
export const trackLeadStatusChanged = (params: {
  fromStatus: string;
  toStatus: string;
  source: string;
  product: string;
}): void => {
  void trackFeatureUsage(FEATURE, "lead_status_changed", {
    from_status: params.fromStatus,
    to_status: params.toStatus,
    lead_source: params.source,
    product: params.product,
  });
};

export const trackLeadStatusChangeFailed = (toStatus: string): void => {
  void trackFeatureUsage(FEATURE, "lead_status_change_failed", {
    to_status: toStatus,
  });
};

/** Lead convertido en contacto: el final feliz del embudo. */
export const trackLeadConverted = (params: {
  source: string;
  product: string;
  score: number;
}): void => {
  void trackFeatureUsage(FEATURE, "lead_converted", {
    lead_source: params.source,
    product: params.product,
    score: params.score,
  });
};

export const trackLeadConversionFailed = (): void => {
  void trackFeatureUsage(FEATURE, "lead_conversion_failed", {});
};

/**
 * Movimiento del lead entre columnas del pipeline.
 *
 * `terminal` (`open`/`won`/`lost`) permite calcular tasa de cierre sin tocar
 * la base de datos, y `method` separa el arrastre del selector del detalle.
 */
export const trackLeadStageMoved = (params: {
  fromStatus: string;
  toStatus: string;
  terminal: string;
  method: LeadMoveMethod;
}): void => {
  void trackFeatureUsage("crm_pipeline", "lead_stage_moved", {
    from_status: params.fromStatus,
    to_status: params.toStatus,
    terminal: params.terminal,
    method: params.method,
  });
};

/**
 * El vendedor abrio el dialogo de "perdido" y se arrepintio.
 *
 * Distingue el arrastre accidental hacia la columna terminal de una perdida
 * real, que es justo lo que hoy ensucia el reporte de cierre.
 */
export const trackLeadStageMoveAbandoned = (toStatus: string): void => {
  void trackFeatureUsage("crm_pipeline", "lead_stage_move_abandoned", {
    to_status: toStatus,
  });
};

/**
 * Calificacion humana de un lead.
 *
 * `hadAiQualification` mide si el agente de IA esta sirviendo de algo: si el
 * humano siempre recalifica, la calificacion automatica no se esta usando.
 */
export const trackLeadQualified = (params: {
  grade: string;
  score: number;
  hasNotes: boolean;
  hadAiQualification: boolean;
}): void => {
  void trackFeatureUsage(FEATURE, "lead_qualified", {
    grade: params.grade,
    score: params.score,
    has_notes: params.hasNotes,
    had_ai_qualification: params.hadAiQualification,
  });
};

/**
 * Importacion masiva de leads.
 *
 * Nunca viaja el nombre del archivo (suele ser el nombre del cliente): solo
 * su extension y los conteos del resultado.
 */
export const trackLeadsImported = (params: {
  fileType: string;
  created: number;
  skipped: number;
}): void => {
  void trackFeatureUsage(FEATURE, "leads_imported", {
    file_type: params.fileType,
    created: params.created,
    skipped: params.skipped,
  });
};

export const trackLeadsImportFailed = (fileType: string): void => {
  void trackFeatureUsage(FEATURE, "leads_import_failed", {
    file_type: fileType,
  });
};

// ============================================================================
// Funnel de captación en la consola (CC-CONSOLE #1542): enlaces cortos, menú de
// bienvenida, tarjeta de origen y motivo de pérdida. Van sólo a PostHog.
// ============================================================================

/**
 * CRM: eventos de la consola para el funnel de captación (#1539, contrato
 * PostHog §3). Los eventos del lado del servidor (lead_created, deal_won…)
 * los manda la API; aquí sólo lo que hace el equipo comercial en pantalla.
 *
 * Van sólo a PostHog (no a Firebase): son los pasos del embudo del contrato y
 * así este módulo no arrastra la inicialización de Firebase a las pantallas.
 *
 * Privacidad: un lead es un prospecto con nombre, correo, teléfono y empresa.
 * Nada de eso sale: viajan ids, canal, producto/marca, formatos y banderas.
 */

type Params = Record<string, string | number | boolean>;

const trackEvent = (name: string, params: Params): void => capturePostHogEvent(name, params);

/** «Coding Cloud» → «coding-cloud», «Wayak» → «wayak» (igual que la API). */
export function brandForProduct(product?: string | null): string | undefined {
  if (!product || !product.trim()) return undefined;
  return product
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Quita los valores vacíos (PostHog/GA4 no aceptan null ni undefined). */
function clean(params: Record<string, string | number | boolean | null | undefined>): Params {
  const out: Params = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== "") out[k] = v;
  }
  return out;
}

interface LeadRef {
  id: string;
  product?: string | null;
  channel?: string | null;
  source?: string | null;
  attribution?: { channel?: string | null; utm_campaign?: string | null; ref?: string | null } | null;
}

/** Se abrió el detalle de un lead. */
export const trackCrmLeadOpened = (lead: LeadRef): void => {
  trackEvent(
    "crm_lead_opened",
    clean({
      lead_id: lead.id,
      product: lead.product,
      brand: brandForProduct(lead.product),
      channel: lead.channel || lead.attribution?.channel,
      source: lead.source,
      has_attribution: Boolean(lead.attribution),
    }),
  );
};

/** Se vio la tarjeta «Origen» (canal, campaña, ref) de un lead. */
export const trackCrmOriginCardViewed = (lead: LeadRef): void => {
  trackEvent(
    "crm_origin_card_viewed",
    clean({
      lead_id: lead.id,
      brand: brandForProduct(lead.product),
      channel: lead.channel || lead.attribution?.channel,
      has_attribution: Boolean(lead.attribution),
      has_campaign: Boolean(lead.attribution?.utm_campaign),
      has_ref: Boolean(lead.attribution?.ref),
    }),
  );
};

/** Se creó un enlace corto / QR. */
export const trackShortLinkCreated = (link: { code: string; channel?: string | null; product?: string | null; utm?: { campaign?: string | null } | null }): void => {
  trackEvent(
    "short_link_created",
    clean({
      code: link.code,
      channel: link.channel,
      product: link.product,
      brand: brandForProduct(link.product),
      has_campaign: Boolean(link.utm?.campaign),
    }),
  );
};

/** Se descargó el QR de un enlace corto. */
export const trackShortLinkQrDownloaded = (format: "svg" | "png", link: { code: string; channel?: string | null }): void => {
  trackEvent("short_link_qr_downloaded", clean({ format, code: link.code, channel: link.channel }));
};

/** Se guardó el menú de bienvenida del chat. */
export const trackWelcomeMenuSaved = (menu: { enabled: boolean; optionCount: number; product?: string | null }): void => {
  trackEvent("welcome_menu_saved", clean({ enabled: menu.enabled, option_count: menu.optionCount, product: menu.product, brand: brandForProduct(menu.product) }));
};

/** Se eligió el motivo de pérdida de un negocio (el id del catálogo; la nota no sale). */
export const trackDealLostReasonSelected = (lostReason: string, hasNote: boolean): void => {
  trackEvent("deal_lost_reason_selected", { lost_reason: lostReason, has_note: hasNote });
};
